import { parseSet, normalizeLoad } from './sets';
import type { WeightUnit, WorkoutSection, SectionType } from './types';
import { normalizeDate, type ParsedSource } from './importClients';

/* ============================================================
   importHistory — turns a per-client training-log workbook into
   the client's full CRM history: Sessions (from dated workout
   tabs), CheckIns (from transposed weekly check-in tabs), and a
   diet-protocol coaching note.

   Reliability contract: AI may CLASSIFY tabs (role hints from
   the ai-import call), but every number — weights, reps, dates,
   body-fat — is transcribed by this code, never by a model.
   Sets go through the same parseSet the live logger uses, so
   imported history reads exactly like logged history.

   Id generation differs per platform (web crypto vs expo-crypto),
   so the caller injects `makeId`; each app's thin importHistory
   wrapper supplies its own.
============================================================ */

export type TabRole = 'workouts' | 'checkins' | 'profile' | 'diet' | 'skip';

export interface ExtractedSession {
  date: string;             // ISO yyyy-mm-dd
  title: string;
  sections: WorkoutSection[];
}

export interface ExtractedCheckIn {
  date: string;             // the check-in's own date
  weekOf: string;           // ISO Monday (the app's grouping key)
  weightLbs?: number;
  bodyFatPct?: number;
  skeletalMuscleMass?: number;
  sleep?: string;
  diet?: string;
  recovery?: string;
  notes?: string;
}

export interface HistoryPlan {
  sessions: ExtractedSession[];
  checkIns: ExtractedCheckIn[];
  dietNote?: string;
  tabCounts: Record<TabRole, number>;
}

// The ORIGINAL tab grid — immune to whatever row detectHeaderRow promoted to
// headers (on transposed check-in tabs it can pick a mid-grid row, which would
// silently drop the Date/Weight rows above it).
const fullGrid = (src: ParsedSource): string[][] => [
  ...(src.preHeader || []),
  src.headers,
  ...src.rows,
];

/* ---------- classification ---------- */

// "061524 - Full" → a dated workout tab (the strongest signal there is).
const DATED_TAB = /^(\d{2})(\d{2})(\d{2})\s*[-–]\s*(.*)$/;

export function classifyTab(src: ParsedSource, aiRole?: TabRole): TabRole {
  if (aiRole && aiRole !== 'skip') return aiRole;
  const name = (src.tabName || '').trim();
  if (DATED_TAB.test(name)) return 'workouts';
  if (/check-?ins?/i.test(name)) return 'checkins';
  if (/diet|nutrition|meal/i.test(name)) return 'diet';
  if (/client\s*info|information|profile/i.test(name)) return 'profile';
  // Structural sniff: a transposed check-in grid (Date:/Weight: row labels).
  const labels = fullGrid(src).map((r) => (r[0] || '').trim().toLowerCase());
  if (labels.some((l) => /^date\b/.test(l)) && labels.some((l) => /weight/.test(l))) return 'checkins';
  return aiRole || 'skip';
}

/* ---------- workout tabs ---------- */

const sectionType = (title: string): SectionType => {
  if (/perform|exercise|main|work\s*sets?/i.test(title)) return 'main';
  if (/mobility|prepare|warm|activation|weakness/i.test(title)) return 'mobility';
  if (/cool|stretch/i.test(title)) return 'cooldown';
  return 'custom';
};

// Column-A labels ending in ":" with no set values are section headers
// ("Prepare:", "Perform:", "Build:"); everything else under them is an exercise.
export function extractWorkout(src: ParsedSource, makeId: () => string): ExtractedSession | null {
  const m = (src.tabName || '').trim().match(DATED_TAB);
  if (!m) return null;
  const [, mm, dd, yy, rawTitle] = m;
  const month = parseInt(mm, 10);
  const day = parseInt(dd, 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = `20${yy}-${mm}-${dd}`;

  const grid = fullGrid(src);

  // The set-header row ("Set 1 (Weight in lbs x Reps)" …) names the set/note
  // columns; its first cell is usually the FIRST section label ("Prepare:").
  let setHeaderIdx = -1;
  const setCols: number[] = [];
  let noteCol = -1;
  for (let r = 0; r < Math.min(grid.length, 6) && setHeaderIdx < 0; r++) {
    grid[r].forEach((c, i) => {
      if (i === 0) return;
      if (/set\s*\d/i.test(c)) { setHeaderIdx = r; setCols.push(i); }
      else if (/note/i.test(c)) noteCol = i;
    });
    if (setHeaderIdx < 0) { setCols.length = 0; noteCol = -1; }
  }
  if (setCols.length === 0) {
    const width = Math.max(...grid.map((r) => r.length));
    for (let i = 1; i < Math.min(width, 5); i++) setCols.push(i);
  }

  const sections: WorkoutSection[] = [];
  let current: WorkoutSection | null = null;
  const openSection = (title: string) => {
    current = { id: makeId(), type: sectionType(title), title: title.replace(/:$/, '').trim(), exercises: [] };
    sections.push(current);
  };

  grid.forEach((row, r) => {
    const label = (row[0] || '').trim();
    if (!label) return;
    if (r === setHeaderIdx) { openSection(label); return; }
    const setVals = setCols.map((i) => (row[i] || '').trim()).filter(Boolean);
    if (/:$/.test(label) && setVals.length === 0) {
      openSection(label);
      return;
    }
    if (!current) openSection('Workout:');
    const note = noteCol >= 0 ? (row[noteCol] || '').trim() : '';
    // "26kg" / "135 lb" cells: split the unit off the load so the value stays
    // numeric and the exercise carries an explicit unit.
    let unit: WeightUnit | undefined;
    const sets = setVals.map(parseSet).map((s) => {
      if (!s.load) return s;
      const n = normalizeLoad(s.load);
      if (n.unit) unit = n.unit;
      return n.unit ? { ...s, load: n.load } : s;
    });
    current!.exercises.push({
      id: makeId(),
      name: label,
      note: note || undefined,
      sets,
      unit,
    });
  });

  const kept = sections.filter((s) => s.exercises.length > 0);
  if (kept.reduce((n, s) => n + s.exercises.length, 0) === 0) return null;
  return { date, title: rawTitle.trim() || 'Workout', sections: kept };
}

/* ---------- check-in tabs (transposed: one column per week) ---------- */

const mondayOf = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${String(d.getDate()).padStart(2, '0')}`;
};

export function extractCheckIns(src: ParsedSource): ExtractedCheckIn[] {
  const grid = fullGrid(src);
  const findRow = (rx: RegExp) => grid.find((r) => rx.test((r[0] || '').trim()));
  const dateRow = findRow(/^date\b/i);
  if (!dateRow) return [];

  const out: ExtractedCheckIn[] = [];
  for (let col = 1; col < dateRow.length; col++) {
    const date = normalizeDate((dateRow[col] || '').trim());
    if (!date) continue;
    const cell = (rx: RegExp): string => {
      const r = findRow(rx);
      return r ? String(r[col] ?? '').trim() : '';
    };
    const ci: ExtractedCheckIn = { date, weekOf: mondayOf(date) };

    // "143.8 15.7%" (combined) · "147.6" (weight only) — both appear in the wild.
    const wRaw = cell(/^weight\b/i);
    const combo = wRaw.match(/^([\d.]+)\s+([\d.]+)\s*%?$/);
    if (combo) {
      ci.weightLbs = parseFloat(combo[1]);
      ci.bodyFatPct = parseFloat(combo[2]);
    } else if (wRaw && !isNaN(parseFloat(wRaw))) {
      ci.weightLbs = parseFloat(wRaw);
    }
    const bfRaw = cell(/^body\s*fat/i).replace('%', '');
    if (bfRaw && !isNaN(parseFloat(bfRaw))) {
      const v = parseFloat(bfRaw);
      ci.bodyFatPct = v <= 1 ? Math.round(v * 1000) / 10 : v; // 0.135 → 13.5
    }
    const smm = cell(/skeletal|muscle\s*mass/i);
    if (smm && !isNaN(parseFloat(smm))) ci.skeletalMuscleMass = parseFloat(smm);
    const diet = cell(/^diet/i);
    if (diet) ci.diet = diet;
    const sleep = cell(/^sleep/i);
    if (sleep) ci.sleep = sleep;
    const energy = cell(/^(energy|recovery)/i);
    if (energy) ci.recovery = energy;
    const digestion = cell(/^digestion/i);
    if (digestion) ci.notes = `Digestion: ${digestion}`;

    out.push(ci);
  }
  return out;
}

/* ---------- diet tabs → one coaching note ---------- */

const DIET_NOTE_CAP = 6000;

export function extractDietNote(sources: ParsedSource[]): string | undefined {
  const lines: string[] = [];
  for (const src of sources) {
    for (const row of fullGrid(src)) {
      const line = row.map((c) => (c || '').trim()).filter(Boolean).join(' ');
      if (line) lines.push(line);
    }
  }
  if (lines.length === 0) return undefined;
  const body = lines.join('\n');
  const text = `Diet protocol (imported from sheet):\n${body}`;
  return text.length > DIET_NOTE_CAP ? `${text.slice(0, DIET_NOTE_CAP)}\n…(truncated)` : text;
}

/* ---------- the plan ---------- */

export function planHistory(
  sources: ParsedSource[],
  aiRoles: Record<number, TabRole>,
  makeId: () => string
): HistoryPlan {
  const tabCounts: Record<TabRole, number> = { workouts: 0, checkins: 0, profile: 0, diet: 0, skip: 0 };
  const sessions: ExtractedSession[] = [];
  const checkIns: ExtractedCheckIn[] = [];
  const dietTabs: ParsedSource[] = [];

  sources.forEach((src, i) => {
    const role = classifyTab(src, aiRoles[i]);
    if (role === 'workouts') {
      const s = extractWorkout(src, makeId);
      if (s) { sessions.push(s); tabCounts.workouts++; return; }
      tabCounts.skip++;
      return;
    }
    if (role === 'checkins') {
      const c = extractCheckIns(src);
      if (c.length > 0) { checkIns.push(...c); tabCounts.checkins++; return; }
      tabCounts.skip++;
      return;
    }
    if (role === 'diet') { dietTabs.push(src); tabCounts.diet++; return; }
    tabCounts[role]++;
  });

  sessions.sort((a, b) => a.date.localeCompare(b.date));
  checkIns.sort((a, b) => a.date.localeCompare(b.date));
  return { sessions, checkIns, dietNote: extractDietNote(dietTabs), tabCounts };
}
